import React, { useState } from 'react';
import { Box, Button, Container, Paper, TextField, Typography } from '@mui/material';
import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { useOrganization } from '@/app/contexts/OrganizationContext';
import { useNotification } from '@/app/contexts/NotificationContext';
import { SendMailDocument } from '@/generated/pos-types';

const ACTIVATION_RECIPIENT = 'info@orda-app.com';

export default function NotAReseller() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { currentTenantId } = useOrganization();
  const { showSuccess, showError } = useNotification();
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendMail] = useMutation(SendMailDocument);

  const handleSend = async () => {
    setSending(true);
    try {
      const body = [
        message.trim(),
        '',
        '---',
        `${t('notAReseller.email')}: ${user?.email ?? '–'}`,
        `${t('notAReseller.tenantId')}: ${currentTenantId ?? '–'}`,
      ].join('\n');

      const res = await sendMail({
        variables: {
          sendMail: {
            to: [ACTIVATION_RECIPIENT],
            subject: t('notAReseller.emailSubject'),
            body,
            templateName: 'base_template.html',
            isBodyHtml: true,
            ...(user?.email ? { from: user.email, replyTo: [user.email] } : {}),
          },
        },
        context: { schema: 'PosSchema' },
      });

      if (!res.data?.sendMail?.isSuccess) {
        showError(res.data?.sendMail?.message ?? t('notAReseller.sendError'));
        return;
      }

      setSent(true);
      showSuccess(t('notAReseller.sendSuccess'));
    } catch (e) {
      showError(e instanceof Error ? e.message : t('notAReseller.sendError'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, boxShadow: 2 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            {t('notAReseller.title')}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t('notAReseller.description')}
          </Typography>

          {sent ? (
            <Typography color="success.main" sx={{ mb: 3 }}>
              {t('notAReseller.sendSuccess')}
            </Typography>
          ) : (
            <>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label={t('notAReseller.messageLabel')}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={sending || !message.trim()}
                onClick={() => void handleSend()}
                sx={{ mb: 2 }}
              >
                {sending ? t('Loading...') : t('notAReseller.send')}
              </Button>
            </>
          )}

          <Button fullWidth variant="text" onClick={logout}>
            {t('notAReseller.logout')}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
}
